import { ApolloLink, Observable } from "@apollo/client";

/**
 * Apollo Link that adds per-operation timeout support.
 *
 * Usage:
 * ```typescript
 * const result = await fetchTranscriptionContext({
 *   variables: { chatSessionId },
 *   context: {
 *     timeout: 5000, // 5 second timeout
 *   },
 * });
 * ```
 */
export const timeoutLink = new ApolloLink((operation, forward) => {
  const { timeout } = operation.getContext() as { timeout?: number };

  // If no timeout specified, pass through
  if (!timeout) {
    return forward(operation);
  }

  return new Observable((observer) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      observer.error(new Error(`Operation timed out after ${timeout}ms`));
    }, timeout);

    // Add abort signal to fetch options for the HttpLink
    // Combine with any existing signal so both timeout and external cancellation work
    operation.setContext(({ fetchOptions = {} }: { fetchOptions?: RequestInit }) => {
      const existingSignal = fetchOptions.signal;

      // If there's an existing signal, listen to it and abort our controller when it fires
      if (existingSignal) {
        if (existingSignal.aborted) {
          controller.abort();
        } else {
          existingSignal.addEventListener("abort", () => controller.abort(), { once: true });
        }
      }

      return {
        fetchOptions: {
          ...fetchOptions,
          signal: controller.signal,
        },
      };
    });

    const subscription = forward(operation).subscribe({
      next: (data) => observer.next(data),
      error: (error) => {
        clearTimeout(timeoutId);
        observer.error(error);
      },
      complete: () => {
        clearTimeout(timeoutId);
        observer.complete();
      },
    });

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  });
});
